import jQuery from 'jquery';
import EventHandler from './EventHandler';
import { transition } from './helpers/common';
import debounce from 'lodash/debounce';
import { ProductResults, ProductNoResults, ContentResults, ContentNoResults, ResultsFooter } from './helpers/livesearch';
import { clear, dim } from './helpers/site-main-dimmer';
import Layout from './Layout';
import ScrollLock from './ScrollLock';
import Search from './Search';
import { SearchForm } from './SearchForm';
import { FormatMoney } from '@components/Shared/Utils/price';
const MAX_RESULTS = 7;
export default class LiveSearch {
    constructor(els, settings) {
        this.$el = jQuery(els.el);
        this.$header = jQuery(els.header);
        this.closeEvents = new EventHandler();
        this.closeEventRequestors = new Set();
        this.settings = settings;
        this.enableContent = this.settings.enable_content;
        this.enableImages = this.settings.enable_images;
        this.mobileSearchBar = this.settings.show_mobile_search_bar;
        this.searchType = this.enableContent ? 'article,page,product' : 'product';
        this.category = '';
        const $form = this.$el.find('[data-live-search-form]');
        this.$input = $form.find('[data-live-search-input]');
        this.$button = $form.find('[data-live-search-submit]');
        this.$takeOverButton = $form.find('[data-live-search-takeover-cancel]');
        this.$filter = this.$el.find('[data-live-search-filter]');
        this.$flyDown = this.$el.find('[data-live-search-flydown]');
        this.$searchResults = this.$flyDown.find('[data-live-search-results]');
        this.$searchPlaceholder = this.$flyDown.find('[data-live-search-placeholder]');
        this.$quickLinks = this.$flyDown.find('[data-live-search-quick-links]');
        this._onClose = () => { };
        this.disableAnimations = 'reduceAnimations' in document.body.dataset;
        this.animationFlyDown = transition({
            el: this.$flyDown.get(0),
            state: 'closed',
        });
        this.animationSearchResults = transition({
            el: this.$searchResults.get(0),
            state: 'closed',
        });
        this.animationTakeover = transition({
            el: this.$el[0],
            state: 'closed',
        });
        if (this.$quickLinks.length) {
            this.animationQuickLinks = transition({
                el: this.$quickLinks.get(0),
                state: 'closed',
            });
        }
        this.staticSearch = new SearchForm(this.$el[0], {
            liveSearch: true,
            setCategory: (category) => {
                this.category = category;
            },
        });
        this.search = new Search({
            view: 'header',
            type: this.searchType,
        });
        this._search = this._search.bind(this);
        this._documentFocus = this._documentFocus.bind(this);
        this._searchCancelled = this._searchCancelled.bind(this);
        this._searchComplete = this._searchComplete.bind(this);
        this._searchError = this._searchError.bind(this);
        this.search.on('cancelled', this._searchCancelled);
        this.search.on('complete', this._searchComplete);
        this.search.on('error', this._searchError);
        this._closeEsc = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                this.$input.focus();
                this._closeFlyDown(true);
                this._closeTakeOver();
            }
        };
        this.events = [
            this.$input.on('keyup.live-search', debounce(this._search, 250)),
            this.$input.on('focus.live-search', (event) => {
                event.stopPropagation();
                this._onSearchFocus();
            }),
            this.$takeOverButton.on('click.live-search', (event) => {
                event.preventDefault();
                this._closeFlyDown(true);
                this._closeTakeOver();
            }),
            this.$filter.on('change.live-search', (event) => {
                const terms = this.$input.val();
                const hasTerms = terms.length > 0;
                if (!hasTerms)
                    return;
                this._search(event);
            }),
        ];
    }
    open() {
        // If is XS, pop the search out into a Takeover screen
        if (Layout.isLessThanBreakpoint('S')) {
            this._openTakeOver();
        }
        this._openFlyDown();
        this.$input.focus();
    }
    set onClose(onClose) {
        this._onClose = onClose;
    }
    unload() {
        this.events.forEach(($el) => $el.off('.live-search'));
        this.closeEvents.unregisterAll();
        this.closeEventRequestors.clear();
        this.search.off('cancelled', this._searchCancelled);
        this.search.off('complete', this._searchComplete);
        this.search.off('error', this._searchError);
        if (this.settings.use_dimmer) {
            clear(this);
        }
        this.staticSearch.unload();
        ScrollLock.unlock();
    }
    get _terms() {
        return this.$input.val();
    }
    get _hasTerms() {
        return this._terms.length > 0;
    }
    _onSearchFocus() {
        this.$el.addClass('live-search--focused'); // If is XS, pop the search out into a Takeover screen
        if (Layout.isLessThanBreakpoint('S')) {
            if (!this._hasTerms)
                return;
            this._openTakeOver();
        }
        this._openFlyDown();
    }
    _search(event) {
        // Ignore non character key strokes
        const invalidKeyStrokes = [
            'Alt',
            'ArrowRight',
            'ArrowLeft',
            'ArrowUp',
            'ArrowDown',
            'Capslock',
            'Control',
            'Escape',
            'Meta',
            'Shift',
            'Tab',
            'Enter',
        ];
        if (event.key && invalidKeyStrokes.indexOf(event.key) !== -1) {
            return;
        }
        let terms = this._terms; // If is XS, pop the search out into a Takeover screen
        if (Layout.isLessThanBreakpoint('S')) {
            this._openTakeOver();
        }
        this._toggleButton(this._hasTerms);
    }
    _searchError(response) {
        console.warn('Search had error', response);
        this._toggleButton(false);
    }
    /**
     * Search has been cancelled due to new search being performed
     *
     * @private
     */
    _searchCancelled() {
        // Searches are canceled on user interaction, UI should remain unchanged
    }
    _searchComplete(response) {
        let { content, products } = response;
        const { terms, sanitizedTerms } = response;
        content = content.slice(0, MAX_RESULTS);
        products = products.slice(0, MAX_RESULTS);
        const { enable_images: enableImages, context } = this.settings;
        const settings = {
            products: {
                results: {
                    products,
                    enableImages,
                },
                noResults: {
                    context,
                    terms: sanitizedTerms,
                    category: this.category,
                },
            },
            content: {
                results: {
                    context,
                    content,
                },
                noResults: {
                    context,
                },
            },
            footer: {
                context,
                terms,
                type: this.searchType,
            },
        };
        const $productResults = products.length ? ProductResults(settings.products.results) : ProductNoResults(settings.products.noResults);
        const $contentResults = content.length ? ContentResults(settings.content.results) : ContentNoResults(settings.content.noResults);
        $productResults.find('.money').each((i, el) => {
            el.innerHTML = FormatMoney(
            // eslint-disable-line no-param-reassign
            Number(el.innerHTML), this.settings.money_format);
        }); // Show results
        this.$searchResults.html($productResults);
        if (this.enableContent) {
            this.$searchResults.append($contentResults);
        }
        this.$searchResults.append(ResultsFooter(settings.footer));
        this._openFlyDown();
        this._toggleButton(false);
    }
    /**
     * Toggles search button while processing
     *
     * @param disable
     * @private
     */
    _toggleButton(disable) {
        if (disable) {
            this.$button.addClass('search-icon--processing').attr('disabled');
        }
        else {
            this.$button.removeClass('search-icon--processing').removeAttr('disabled');
        }
    }
    _shouldOpenFlyDown(placeholder = false) {
        if (placeholder)
            return true;
        const hasTerms = this.$input.val().length > 0;
        const hasNoResults = this.$searchResults.find('[data-live-search-no-products]').length > 0;
        const hasResults = this.$searchResults.children().length > 0;
        const hasQuickLinks = this.$quickLinks.length;
        return (hasTerms && (hasResults || hasNoResults)) || (!hasTerms && hasQuickLinks);
    }
    _registerCloseEvents(requestor) {
        if (this.closeEventRequestors.size === 0) {
            this.closeEvents.register(window, 'keydown', this._closeEsc);
            this.closeEvents.register(document, 'focusin', this._documentFocus);
            this.closeEvents.register(document, 'touchstart', this._documentFocus);
            this.closeEvents.register(document, 'click', this._documentFocus);
        }
        this.closeEventRequestors.add(requestor);
    }
    _unregisterCloseEvents(requestor) {
        this.closeEventRequestors.delete(requestor);
        if (this.closeEventRequestors.size)
            return;
        this.closeEvents.unregisterAll();
        this._onClose();
    }
    _openFlyDown(placeholder = false) {
        if (!this._shouldOpenFlyDown(placeholder))
            return;
        const resize = ({ el }) => {
            // When this function is called, element is `display: block; height: 0;`
            // but the wrapper's scrollHeight is the height we want to transition to
            const container = el.querySelector(':scope > .visible');
            const scrollHeight = container ? container.scrollHeight : 0;
            el.style.setProperty('--open-height', `${scrollHeight}px`);
        };
        this._updateFlyDown(placeholder);
        if (!this.$flyDown.data('is-open')) {
            this._registerCloseEvents('flydown');
            if (this.settings.use_dimmer) {
                dim(this);
            }
            this.$flyDown.data('is-open', true);
            this.animationFlyDown
                .animateTo('open', {
                force: this.disableAnimations,
                onStart: resize,
            })
                .then(() => this.$el.addClass('live-search--active'));
        }
        else {
            resize({
                el: this.$flyDown.get(0),
            });
        }
    }
    _updateFlyDown(placeholder = false) {
        const hasTerms = this.$input.val().length > 0;
        const hasNoResults = this.$searchResults.find('[data-live-search-no-products]').length > 0;
        const hasResults = this.$searchResults.children().length > 0;
        const hasQuickLinks = this.$quickLinks.length;
        if (placeholder) {
            this.$searchResults.removeClass('visible');
            this.$quickLinks.removeClass('visible');
            this.$searchPlaceholder.addClass('visible');
            if (this.animationQuickLinks) {
                this.animationQuickLinks.animateTo('hidden', {
                    force: this.disableAnimations,
                });
            }
            this.animationSearchResults.animateTo('hidden', {
                force: this.disableAnimations,
            });
        }
        else if (hasTerms && (hasNoResults || hasResults)) {
            this.$searchPlaceholder.removeClass('visible');
            this.$quickLinks.removeClass('visible');
            this.$searchResults.addClass('visible');
            if (this.animationQuickLinks) {
                this.animationQuickLinks.animateTo('hidden', {
                    force: this.disableAnimations,
                });
            }
            this.animationSearchResults.animateTo('visible', {
                force: this.disableAnimations,
            });
        }
        else if (hasQuickLinks) {
            this.$searchPlaceholder.removeClass('visible');
            this.$searchResults.removeClass('visible');
            this.$quickLinks.addClass('visible');
            this.animationSearchResults.animateTo('hidden', {
                force: this.disableAnimations,
            });
            this.animationQuickLinks.animateTo('visible', {
                force: this.disableAnimations,
            });
        }
    }
    /**
     * Close the FlyDown when no longer needed
     *  - Keep focus styling if input is still being interacted with
     *
     * @param retainFocus
     * @private
     */
    _closeFlyDown(retainFocus = false) {
        if (!this.$flyDown.data('is-open')) {
            return;
        }
        this._unregisterCloseEvents('flydown');
        clear(this);
        this.$flyDown.data('is-open', false);
        this.$searchPlaceholder.removeClass('visible');
        this.$quickLinks.removeClass('visible');
        this.$searchResults.removeClass('visible');
        if (this.animationQuickLinks) {
            this.animationQuickLinks.animateTo('closed', {
                force: this.disableAnimations,
            });
        }
        this.animationSearchResults.animateTo('closed', {
            force: this.disableAnimations,
        });
        this.animationFlyDown
            .animateTo('closed', {
            force: this.disableAnimations,
            onStart: ({ el }) => el.style.setProperty('--open-height', '0'),
        })
            .then(() => {
            this.$el.removeClass('live-search--active');
            if (!retainFocus) {
                this.$el.removeClass('live-search--focused');
            }
        });
    }
    _openTakeOver() {
        if (this.animationTakeover.state === 'open')
            return;
        if (this.$header.hasClass('search--section')) {
            document.body.classList.add('search-takeover-active');
        }
        ScrollLock.lock(this.$el[0]);
        this.$el.addClass('live-search--takeover');
        this._registerCloseEvents('takeover');
        if (this.settings.use_dimmer) {
            dim(this);
        }
        if (this.mobileSearchBar) {
            // "Expand out" style
            const el = this.$el[0];
            const { top, right, left, width } = el.getBoundingClientRect();
            el.style.setProperty('--live-search-takeover-initial-top', `${top}px`);
            el.style.setProperty('--live-search-takeover-initial-right', `${window.innerWidth - right}px`);
            el.style.setProperty('--live-search-takeover-initial-left', `${left}px`);
            el.style.setProperty('--live-search-takeover-initial-width', `${width}px`);
            el.parentNode.style.height = `${el.parentNode.getBoundingClientRect().height}px`;
            this.animationTakeover.animateTo('open', {
                force: this.disableAnimations,
            });
        }
        else {
            // "Slide up" style
            this.animationTakeover.animateTo('open', {
                force: this.disableAnimations,
                hold: true,
            });
        }
    }
    _closeTakeOver() {
        if (this.animationTakeover.state === 'closed')
            return;
        this._unregisterCloseEvents('takeover');
        if (this.settings.use_dimmer) {
            clear(this);
        }
        this.animationTakeover
            .animateTo('closed', {
            force: this.disableAnimations,
        })
            .then(() => {
            ScrollLock.unlock();
            document.body.classList.remove('search-takeover-active');
            this.$el.removeClass('live-search--takeover');
            if (this.mobileSearchBar) {
                this.$el[0].parentNode.style.height = '';
            }
        });
    }
    /**
     * When the focus element has changed, either by clicking, touching, or tabbing
     * check to see if it is within the flydown
     *
     * @param event
     * @private
     */
    _documentFocus(event) {
        const $closest = jQuery(event.target).closest('[data-live-search]');
        if ($closest.length) {
            return;
        }
        this._closeFlyDown();
        this._closeTakeOver();
    }
}
